/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import moment, { Moment } from 'moment'
import { DatePicker } from '@material-ui/pickers'
import { FormControl, InputLabel, LinearProgress } from '@material-ui/core'
import { MultiSelect } from 'react-multi-select-component'
import Divider from '@material-ui/core/Divider'

import { Dialog, UploadButton, snackbar } from 'components'
import { useApiCall, useCheckFormErrors } from 'hooks'
import { STRINGS } from 'consts'
import { Tag } from 'features/Main/Tags/types'
import { resizeFile } from 'utils'
import { PrizeCategory } from 'consts/prize'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { PartnerIntegration } from 'features/Main/components/PartnerIntegration'

import {
  Coupon,
  UpdateCouponResponse,
  UpdateCouponData,
  DataParams,
  DataResponse,
} from '../../types'
import { fetchPushUserCount, updateCoupon } from '../../api'
import { useStyles } from './styles'

interface ValidationData {
  toteatEnabled: boolean
  category?: PrizeCategory
}

interface TagOption {
  label: string
  value: number
}

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  accentDescription: [
    {
      validate: (value: string) => value.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string) => (value && value.length <= 100) || !value.length,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  description: [
    {
      validate: (value: string) => value.length <= 254,
      message: 'No puede superar los 254 caracteres',
    },
  ],
  expiryDate: [
    {
      validate: (value: Moment | null) => !!value && value.isSameOrAfter(moment().startOf('day')),
      message: 'La fecha de vencimiento no puede ser anterior a hoy',
    },
  ],
  order: [
    {
      validate: (value: string) => (value && Number(value) > 0) || !value,
      message: 'El orden debe ser mayor a 0',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID Beneficio Toteat es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
}

interface UpdateCouponDialogProps {
  handleClose: () => void
  couponToUpdate: Coupon
  onDone: (newCoupon: Coupon) => void
  tags: Tag[]
}

const UpdateCouponDialog: React.FC<UpdateCouponDialogProps> = ({
  handleClose,
  onDone,
  couponToUpdate,
  tags,
}) => {
  const classes = useStyles()

  const [toteatBenefitId, setToteatBenefitId] = useState<string>(
    couponToUpdate.toteatBenefitId || '',
  )
  const [currentSku, setCurrentSku] = useState<string>('')
  const [skusList, setSkusList] = useState<string[]>(
    couponToUpdate.skus ? couponToUpdate.skus.map(s => s.sku) : [],
  )
  const [storesEnabled, setStoresEnabled] = useState(couponToUpdate.storesEnabled)
  const [tucanEnabled, setTucanEnabled] = useState(couponToUpdate.tucanEnabled)
  const [toteatEnabled, setToteatEnabled] = useState(couponToUpdate.toteatEnabled)
  const [category, setCategory] = useState<PrizeCategory | undefined>(couponToUpdate.category)
  const [title, setTitle] = useState(couponToUpdate.title)
  const [accentDescription, setAccentDescription] = useState(couponToUpdate.accentDescription)
  const [additionalText, setAdditionalText] = useState(couponToUpdate.additionalText)
  const [description, setDescription] = useState(couponToUpdate.description ?? '')
  const [image, setImage] = useState<File | undefined>(undefined)
  const [imagePreview, setImagePreview] = useState(couponToUpdate.imageUrl)
  const [icon, setIcon] = useState<File | undefined>(undefined)
  const [iconPreview, setIconPreview] = useState(couponToUpdate.iconUrl)
  const [expiryDate, setExpiryDate] = useState<Moment | null>(moment(couponToUpdate.expiryDate))
  const [order, setOrder] = useState(couponToUpdate.order?.toString() ?? '')
  const [tagsSelected, setTagsSelected] = useState<TagOption[]>([])
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(couponToUpdate.allTagsAreRequired)
  const [userCount, setUserCount] = useState<number | null>(null)
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))

  useEffect(() => {
    setTagsSelected(couponToUpdate.tags.map(t => ({ label: t.title, value: t.id })))
  }, [])

  const handleSetTags = async (e: TagOption[]) => {
    setTagsSelected(e)
  }

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = async () => {
    setAllTagsAreRequired(!allTagsAreRequired)
  }

  const [requestData, isRequestingData] = useApiCall<DataParams, DataResponse>(fetchPushUserCount)

  const handleFetchPushUserCount = async () => {
    try {
      const tagsSelectedId = tagsSelected.map((tag: { value: number }) => tag.value)

      const data = await requestData({
        tagIds: tagsSelectedId,
        allTagsAreRequired,
      })

      setUserCount(data.count)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo calcular la cantidad de usuarios. Intente de nuevo.')
    }
  }

  useEffect(() => {
    handleFetchPushUserCount()
  }, [tagsSelected, allTagsAreRequired])

  const [updateCouponApi, isLoading] = useApiCall<UpdateCouponData, UpdateCouponResponse>(
    updateCoupon,
  )

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setTitle(e.target.value)
  }

  const handleDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setDescription(e.target.value)
  }

  const handleAccentDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setAccentDescription(e.target.value)
  }

  const handleAdditionalTextChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setAdditionalText(e.target.value)
  }

  const handleDateChange = (newDate: Moment | null) => {
    setExpiryDate(newDate)
  }

  const handleImageChange = async (file: File) => {
    setImagePreview(URL.createObjectURL(file))
    const newImage = await resizeFile(file, 600, 600)
    setImage(newImage)
  }

  const handleIconChange = async (file: File) => {
    setIconPreview(URL.createObjectURL(file))
    const newIcon = await resizeFile(file, 192, 192)
    setIcon(newIcon)
  }

  const handleOrderChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setOrder(e.target.value)
  }

  const getRequiredFields = () => {
    const requiredFields = [
      'title',
      'accentDescription',
      'image',
      'icon',
      'expiryDate',
      'additionalText',
    ]

    if (toteatEnabled) {
      if (category === PrizeCategory.General) {
        requiredFields.push('skusList')
      } else if (category === PrizeCategory.Checkout) {
        requiredFields.push('toteatBenefitId')
      }
    }

    return requiredFields
  }

  const fields = {
    title,
    accentDescription,
    description,
    expiryDate,
    order,
    additionalText,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    category,
  }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )
  const isSubmitDisabled = hasErrors || isAnyFieldEmpty || isLoading || hasPartnerErrors

  const onUpdateCoupon = async () => {
    const coupon = {
      title,
      accentDescription,
      description,
      image,
      icon,
      expiryDate: expiryDate?.toISOString(),
      order,
      additionalText,
      tagsSelected,
      allTagsAreRequired,
      category,
      storesEnabled,
      tucanEnabled,
      toteatEnabled,
      skus: skusList,
      toteatBenefitId,
    }
    const payload = {
      id: couponToUpdate.id,
      payload: coupon,
    }
    try {
      const { coupon: updatedCoupon } = await updateCouponApi(payload)
      onDone(updatedCoupon)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo editar la promo. Intente de nuevo.')
    }
  }

  const handlePartnerValidationChange = (hasErrorsPartner: boolean) => {
    setHasPartnerErrors(hasErrorsPartner)
  }

  return (
    <Dialog
      title="Editar promo"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Editar"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onUpdateCoupon}
    >
      <Grid container spacing={3}>
        <PartnerIntegration
          storesEnabled={storesEnabled}
          setStoresEnabled={setStoresEnabled}
          tucanEnabled={tucanEnabled}
          setTucanEnabled={setTucanEnabled}
          toteatEnabled={toteatEnabled}
          setToteatEnabled={setToteatEnabled}
          category={category}
          setCategory={setCategory}
          currentSku={currentSku}
          setCurrentSku={setCurrentSku}
          skusList={skusList}
          setSkusList={setSkusList}
          toteatBenefitId={toteatBenefitId}
          setToteatBenefitId={setToteatBenefitId}
          errors={errors}
          isLoading={isLoading}
          onValidationChange={handlePartnerValidationChange}
        />

        <Grid item xs={12}>
          <Divider variant="middle" />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Título"
            value={title}
            fullWidth
            onChange={handleTitleChange}
            disabled={isLoading}
            required
            error={errors.title.hasError}
            helperText={errors.title.message}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Descripción corta"
            value={accentDescription}
            fullWidth
            onChange={handleAccentDescriptionChange}
            disabled={isLoading}
            required
            error={errors.accentDescription.hasError}
            helperText={errors.accentDescription.message}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Descripción larga"
            value={description}
            fullWidth
            onChange={handleDescriptionChange}
            disabled={isLoading}
            multiline
            error={errors.description.hasError}
            helperText={errors.description.message}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Texto adicional"
            value={additionalText}
            fullWidth
            onChange={handleAdditionalTextChange}
            disabled={isLoading}
            required
            error={errors.additionalText.hasError}
            helperText={errors.additionalText.message}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            value={expiryDate}
            onChange={handleDateChange}
            autoOk
            disablePast
            format={STRINGS.DATE_FORMAT}
            initialFocusedDate={new Date()}
            disabled={isLoading}
            style={{ width: '100%' }}
            label="Vencimiento"
            minDateMessage="La fecha de vencimiento no puede ser anterior a hoy"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            value={order}
            onChange={handleOrderChange}
            label="Order"
            type="number"
            fullWidth
            error={errors.order.hasError}
            helperText={errors.order.message}
            InputProps={{
              inputProps: { min: 1 },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel className={classes.multiselectTitle}>Tag</InputLabel>
          <FormControl className={classes.multiselect}>
            <MultiSelect
              options={tagsForSelect}
              value={tagsSelected}
              onChange={handleSetTags}
              labelledBy="Seleccionar Tags"
              hasSelectAll={false}
            />
          </FormControl>
        </Grid>
        <Grid item xs={11}>
          <CustomFormControlLabel
            control={
              <CustomCheckbox
                onChange={handleAllTagsAreRequiredChange}
                checked={allTagsAreRequired}
              />
            }
            label="Sólo usuarios con todos los tags seleccionados"
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <div className={classes.iconUploadContainer}>
            <UploadButton
              id="coupon-icon-upload-button"
              accept=".jpg, .jpeg, .png"
              label="Subir ícono"
              onChange={handleIconChange}
            />
            <div className={classes.iconUploadPreviewContainer}>
              <img src={iconPreview} alt="" className={classes.iconPreview} />
            </div>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.imageUploadContainer}>
            <UploadButton
              id="coupon-image-upload-button"
              accept=".jpg, .jpeg, .png"
              label="Subir imagen"
              onChange={handleImageChange}
            />
            <div className={classes.imageUploadPreviewContainer}>
              <img src={imagePreview} alt="" className={classes.imagePreview} />
            </div>
          </div>
        </Grid>
        <Grid item xs={11}>
          <Divider variant="middle" />
        </Grid>
        <Grid item xs={12}>
          <InputLabel>
            {isRequestingData ? (
              <LinearProgress style={{ marginTop: 25, marginBottom: 25 }} />
            ) : (
              <div className={classes.tootlTipUsers}>
                Esta acción impactará a {userCount} usuarios
              </div>
            )}
          </InputLabel>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { UpdateCouponDialog }
